import { ActivityIcon, UserIcon } from 'lucide-react';
import Modal from '@/components/Modal';
import FormField from '@/components/form/FormField';
import Input from '@/components/form/Input';
import ProfileImageUpload from './ProfileImageUpload';

interface EditProfileModalProps {
  isOpen: boolean;
  onClose: () => void;
  username: string;
  avatarUrl: string;
  onUsernameChange: (value: string) => void;
  onAvatarUrlChange: (value: string) => void;
  onSubmit: () => void;
  isUpdating: boolean;
  error?: string;
  userId: string;
}

export default function EditProfileModal({
  isOpen,
  onClose,
  username,
  avatarUrl,
  onUsernameChange,
  onAvatarUrlChange,
  onSubmit,
  isUpdating,
  error,
  userId
}: EditProfileModalProps) {
  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Edit Profile"
    >
      <div className="space-y-6">
        <div className="flex flex-col items-center mb-6">
          <div className="h-24 w-24 rounded-full overflow-hidden mb-4">
            <ProfileImageUpload
              currentImageUrl={avatarUrl}
              userId={userId}
              onImageUpdated={onAvatarUrlChange}
            />
          </div>
          <p className="text-sm text-gray-500">
            Click to upload or update your profile picture
          </p>
        </div>

        <FormField 
          label="Username" 
          required 
          error={error}
          helpText="3-30 characters, letters, numbers, underscores, and hyphens only"
        >
          <Input
            type="text"
            value={username}
            onChange={(e) => onUsernameChange(e.target.value)}
            placeholder="Enter username"
            pattern="[a-zA-Z0-9_-]+"
            minLength={3}
            maxLength={30}
            required
            error={!!error}
          />
        </FormField>

        <div className="flex justify-end space-x-3 pt-4 border-t">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-gray-700 hover:text-gray-900 focus:outline-none"
          >
            Cancel
          </button>
          <button
            onClick={onSubmit}
            disabled={isUpdating}
            className="inline-flex items-center px-6 py-3 border border-transparent rounded-lg shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {isUpdating ? (
              <>
                <ActivityIcon className="animate-spin -ml-1 mr-2 h-4 w-4" />
                Updating...
              </>
            ) : (
              'Save Changes'
            )}
          </button>
        </div>
      </div>
    </Modal>
  );
}